import { useCallback, useEffect, useState } from "react";
import api from "../lib/api";
import { saveAs } from "../lib/fileSaver";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { FileBadge, Loader2, Search, FileText, FileDown, Trash2, ChevronDown, ChevronUp } from "lucide-react";
import { AutodichiarazionePreview } from "../components/AutodichiarazionePreview";

const labelCls = "text-xs font-semibold uppercase tracking-wide text-slate-700";
const selectCls = "mt-1 h-9 w-full rounded-md border border-slate-200 bg-white px-2 text-sm";
const fmtD = (iso) => (iso ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}` : "—");
const fmtDT = (iso) => { try { return new Date(iso).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" }); } catch { return iso; } };
const thisMonth = () => new Date().toISOString().slice(0, 7);

const download = async (rec, fmt) => {
    try {
        const res = await api.get(`/autodichiarazioni/${rec.id}/download`, { params: { fmt }, responseType: "blob" });
        saveAs(res.data, `${rec.filename_base}.${fmt}`);
    } catch { toast.error("Errore nel download"); }
};

function HistoryTable({ items, onDelete }) {
    if (!items.length) return <div className="rounded-md border border-dashed border-slate-200 px-4 py-6 text-center text-sm text-slate-400" data-testid="ad-history-empty">Nessuna autodichiarazione prodotta finora.</div>;
    return (
        <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white shadow-sm">
            <table className="w-full min-w-[820px] text-sm" data-testid="ad-history-table">
                <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                    <tr>
                        <th className="px-3 py-2">Prot.</th><th className="px-3 py-2">Data</th><th className="px-3 py-2">Cliente / cantiere</th>
                        <th className="px-3 py-2">Periodo</th><th className="px-3 py-2 text-right">Dipendenti</th><th className="px-3 py-2 text-right">Buste mancanti</th>
                        <th className="px-3 py-2">Creata</th><th className="px-3 py-2"></th>
                    </tr>
                </thead>
                <tbody>
                    {items.map((r) => (
                        <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`ad-history-row-${r.id}`}>
                            <td className="px-3 py-2 font-mono-numbers font-semibold text-slate-700">{r.protocollo}</td>
                            <td className="px-3 py-2 font-mono-numbers text-slate-600">{fmtD(r.data)}</td>
                            <td className="px-3 py-2 text-slate-700">{r.target_name}{r.appalto ? <div className="text-[11px] text-slate-400">{r.appalto}</div> : null}</td>
                            <td className="px-3 py-2 text-slate-600">{r.periodo_label}</td>
                            <td className="px-3 py-2 text-right font-mono-numbers">{r.employee_count}</td>
                            <td className={`px-3 py-2 text-right font-mono-numbers ${r.missing_payslips ? "font-semibold text-amber-700" : "text-slate-400"}`}>{r.missing_payslips || 0}</td>
                            <td className="px-3 py-2 text-xs text-slate-500">{fmtDT(r.created_at)}<div className="text-[10px] text-slate-400">{r.created_by}</div></td>
                            <td className="px-3 py-2">
                                <div className="flex items-center justify-end gap-1">
                                    <Button variant="outline" size="sm" className="h-7 gap-1 text-xs" onClick={() => download(r, "pdf")} data-testid={`ad-dl-pdf-${r.id}`}><FileDown className="h-3.5 w-3.5" /> PDF</Button>
                                    <Button variant="outline" size="sm" className="h-7 gap-1 text-xs" onClick={() => download(r, "docx")} data-testid={`ad-dl-docx-${r.id}`}><FileText className="h-3.5 w-3.5" /> Word</Button>
                                    <Button variant="ghost" size="icon" className="h-7 w-7 text-slate-400 hover:text-red-600" onClick={() => onDelete(r)} data-testid={`ad-delete-${r.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                                </div>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

export default function AutodichiarazioniPage() {
    const [defaults, setDefaults] = useState(null);
    const [history, setHistory] = useState([]);
    const [targetType, setTargetType] = useState("gruppo");
    const [targetId, setTargetId] = useState("");
    const [from, setFrom] = useState(thisMonth());
    const [to, setTo] = useState(thisMonth());
    const [committente, setCommittente] = useState("");
    const [appalto, setAppalto] = useState("");
    const [protocollo, setProtocollo] = useState("");
    const [data, setData] = useState(new Date().toISOString().slice(0, 10));
    const [luogo, setLuogo] = useState("Taranto");
    const [dich, setDich] = useState({ nome: "", luogo_nascita: "", data_nascita: "", codice_fiscale: "" });
    const [showDich, setShowDich] = useState(false);
    const [preview, setPreview] = useState(null);
    const [excluded, setExcluded] = useState([]);
    const [extraIds, setExtraIds] = useState([]);
    const [loadingPreview, setLoadingPreview] = useState(false);
    const [generating, setGenerating] = useState(false);
    const [lastRec, setLastRec] = useState(null);

    const loadHistory = useCallback(() => api.get("/autodichiarazioni").then((r) => setHistory(r.data)).catch(() => {}), []);
    useEffect(() => {
        api.get("/autodichiarazioni/defaults").then((r) => {
            setDefaults(r.data);
            setDich({ nome: "", luogo_nascita: "", data_nascita: "", codice_fiscale: "", ...(r.data.dichiarante || {}) });
            setLuogo(r.data.luogo || "Taranto");
            setProtocollo(r.data.next_protocollo || "");
        }).catch(() => toast.error("Errore nel caricamento"));
        loadHistory();
    }, [loadHistory]);

    useEffect(() => { setPreview(null); setExcluded([]); setExtraIds([]); }, [targetType, targetId, from, to]);

    const targetParams = () => (targetType === "gruppo" ? { group_id: targetId } : { worksite_id: targetId });

    const loadPreview = async (extras = extraIds) => {
        if (!targetId) { toast.error("Seleziona il cliente/gruppo o il cantiere"); return; }
        setLoadingPreview(true);
        try {
            const { data: d } = await api.get("/autodichiarazioni/preview", { params: { ...targetParams(), period_from: from, period_to: to, extra: extras.join(",") || undefined } });
            setPreview(d);
            setExcluded((p) => p.filter((id) => d.employees.some((e) => e.id === id)));
            if (!committente && targetType === "cantiere") {
                const w = defaults?.worksites.find((x) => x.id === targetId);
                if (w?.client_name) setCommittente(w.client_name);
            }
        } catch (e) {
            const det = e.response?.data?.detail; toast.error(typeof det === "string" ? det : "Errore nell'anteprima");
        } finally { setLoadingPreview(false); }
    };

    const generate = async () => {
        if (!preview) { toast.error("Carica prima l'anteprima dell'elenco"); return; }
        setGenerating(true);
        try {
            const { data: rec } = await api.post("/autodichiarazioni", {
                ...targetParams(), period_from: from, period_to: to, committente, appalto, protocollo, data, luogo,
                dichiarante: dich, exclude_employee_ids: excluded, extra_employee_ids: extraIds,
            });
            setLastRec(rec);
            toast.success(`Autodichiarazione prot. ${rec.protocollo} generata (${rec.employee_count} dipendenti)`);
            await loadHistory();
            const { data: d } = await api.get("/autodichiarazioni/defaults");
            setDefaults(d); setProtocollo(d.next_protocollo);
        } catch (e) {
            const det = e.response?.data?.detail; toast.error(typeof det === "string" ? det : "Errore nella generazione");
        } finally { setGenerating(false); }
    };

    const remove = async (r) => {
        if (!window.confirm(`Eliminare l'autodichiarazione prot. ${r.protocollo} (${r.target_name})?`)) return;
        try { await api.delete(`/autodichiarazioni/${r.id}`); toast.success("Eliminata"); loadHistory(); }
        catch { toast.error("Errore nell'eliminazione"); }
    };

    const options = targetType === "gruppo" ? defaults?.groups || [] : defaults?.worksites || [];

    return (
        <div className="mx-auto max-w-6xl px-6 py-8" data-testid="autodichiarazioni-page">
            <div className="mb-6 flex items-start gap-3">
                <div className="flex h-11 w-11 items-center justify-center rounded-md bg-[#376DB3]/10 text-[#376DB3]"><FileBadge className="h-6 w-6" /></div>
                <div>
                    <h1 className="font-display text-2xl font-bold text-slate-800">Autodichiarazione emolumenti</h1>
                    <p className="text-sm text-slate-500">Dichiarazione sostitutiva (artt. 46-47 D.P.R. 445/2000) di regolare pagamento di retribuzioni e contributi al personale di un appalto, su carta intestata, con elenco nominativo e controllo buste paga.</p>
                </div>
            </div>

            <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm" data-testid="ad-form">
                <div className="grid gap-4 md:grid-cols-4">
                    <div>
                        <Label className={labelCls}>Seleziona per</Label>
                        <select value={targetType} onChange={(e) => { setTargetType(e.target.value); setTargetId(""); }} className={selectCls} data-testid="ad-target-type">
                            <option value="gruppo">Gruppo / cliente</option>
                            <option value="cantiere">Cantiere</option>
                        </select>
                    </div>
                    <div className="md:col-span-1">
                        <Label className={labelCls}>{targetType === "gruppo" ? "Gruppo *" : "Cantiere *"}</Label>
                        <select value={targetId} onChange={(e) => setTargetId(e.target.value)} className={selectCls} data-testid="ad-target-select">
                            <option value="">Seleziona…</option>
                            {options.map((o) => <option key={o.id} value={o.id}>{o.name}{o.client_name ? ` — ${o.client_name}` : ""}</option>)}
                        </select>
                    </div>
                    <div>
                        <Label className={labelCls}>Dal mese *</Label>
                        <input type="month" value={from} onChange={(e) => setFrom(e.target.value)} className={selectCls} data-testid="ad-from" />
                    </div>
                    <div>
                        <Label className={labelCls}>Al mese *</Label>
                        <input type="month" value={to} onChange={(e) => setTo(e.target.value)} className={selectCls} data-testid="ad-to" />
                    </div>
                </div>
                <div className="mt-4 grid gap-4 md:grid-cols-2">
                    <div>
                        <Label className={labelCls}>Committente (intestazione "Spett.le")</Label>
                        <Textarea value={committente} onChange={(e) => setCommittente(e.target.value)} rows={3} placeholder={"Ragione sociale\nIndirizzo\nCAP Città"} className="mt-1 text-sm" data-testid="ad-committente" />
                    </div>
                    <div className="grid gap-3 sm:grid-cols-2">
                        <div className="sm:col-span-2">
                            <Label className={labelCls}>Oggetto dell'appalto</Label>
                            <Input value={appalto} onChange={(e) => setAppalto(e.target.value)} placeholder="es. servizi di pulizia presso lo stabilimento di…" className="mt-1 h-9" data-testid="ad-appalto" />
                        </div>
                        <div>
                            <Label className={labelCls}>Prot. n°</Label>
                            <Input value={protocollo} onChange={(e) => setProtocollo(e.target.value)} className="mt-1 h-9 font-mono-numbers" data-testid="ad-protocollo" />
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                            <div>
                                <Label className={labelCls}>Data</Label>
                                <input type="date" value={data} onChange={(e) => setData(e.target.value)} className={selectCls} data-testid="ad-data" />
                            </div>
                            <div>
                                <Label className={labelCls}>Luogo</Label>
                                <Input value={luogo} onChange={(e) => setLuogo(e.target.value)} className="mt-1 h-9" data-testid="ad-luogo" />
                            </div>
                        </div>
                    </div>
                </div>

                <button type="button" onClick={() => setShowDich((v) => !v)} className="mt-4 flex items-center gap-1 text-xs font-semibold text-[#376DB3]" data-testid="ad-dich-toggle">
                    {showDich ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                    Dichiarante (legale rappresentante){dich.nome ? ` — ${dich.nome}` : " — da compilare"}
                </button>
                {showDich && (
                    <div className="mt-2 grid gap-3 rounded-md bg-slate-50 p-3 sm:grid-cols-4" data-testid="ad-dich-box">
                        <div><Label className={labelCls}>Nome e cognome</Label><Input value={dich.nome} onChange={(e) => setDich({ ...dich, nome: e.target.value })} className="mt-1 h-9" data-testid="ad-dich-nome" /></div>
                        <div><Label className={labelCls}>Nato/a a</Label><Input value={dich.luogo_nascita} onChange={(e) => setDich({ ...dich, luogo_nascita: e.target.value })} className="mt-1 h-9" data-testid="ad-dich-luogo" /></div>
                        <div><Label className={labelCls}>Il</Label><input type="date" value={dich.data_nascita || ""} onChange={(e) => setDich({ ...dich, data_nascita: e.target.value })} className={selectCls} data-testid="ad-dich-data" /></div>
                        <div><Label className={labelCls}>Codice fiscale</Label><Input value={dich.codice_fiscale} onChange={(e) => setDich({ ...dich, codice_fiscale: e.target.value.toUpperCase() })} className="mt-1 h-9 font-mono-numbers uppercase" data-testid="ad-dich-cf" /></div>
                        <p className="text-[11px] text-slate-500 sm:col-span-4">I dati del dichiarante vengono ricordati per le prossime autodichiarazioni. Se lasciati vuoti, nel documento restano le righe da compilare a mano.</p>
                    </div>
                )}

                <div className="mt-5 flex flex-wrap items-center gap-2">
                    <Button variant="outline" onClick={() => loadPreview()} disabled={loadingPreview} className="gap-2" data-testid="ad-preview-btn">
                        {loadingPreview ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />} Anteprima elenco dipendenti
                    </Button>
                    <Button onClick={generate} disabled={!preview || generating} className="gap-2 bg-[#376DB3] text-white hover:bg-[#2c5a96]" data-testid="ad-generate-btn">
                        {generating ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileBadge className="h-4 w-4" />} Genera PDF + Word
                    </Button>
                    {lastRec && (
                        <div className="ml-auto flex items-center gap-2 rounded-md bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-700" data-testid="ad-last-result">
                            Prot. {lastRec.protocollo} pronta:
                            <button type="button" className="underline" onClick={() => download(lastRec, "pdf")} data-testid="ad-last-pdf">PDF</button>
                            <button type="button" className="underline" onClick={() => download(lastRec, "docx")} data-testid="ad-last-docx">Word</button>
                        </div>
                    )}
                </div>

                {preview && (
                    <div className="mt-5 border-t border-slate-100 pt-4">
                        <AutodichiarazionePreview preview={preview} excluded={excluded} adding={loadingPreview}
                            onToggle={(id) => setExcluded((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]))}
                            onAdd={(id) => { const next = [...extraIds, id]; setExtraIds(next); loadPreview(next); }} />
                    </div>
                )}
            </div>

            <h2 className="mb-2 mt-8 font-display text-base font-bold text-slate-800">Storico autodichiarazioni</h2>
            <HistoryTable items={history} onDelete={remove} />
        </div>
    );
}
